import { useMemo } from 'react';

import { useCartValues } from '@fhs/cart/src/state/cart-store';
import useCartTotal from '@fhs/cart/src/state/cart-store/pricing-cart/use-cart-total';
import {
  ILoyaltyEstimatePointsInput,
  LoyaltyServiceMode,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import {
  parseEntry,
  parseOffers,
} from '@fhs-legacy/frontend/src/state/global-state/models/loyalty/offers/offers.utils';
import { usePaymentContext } from '@fhs-legacy/frontend/src/state/payment';
import { mapPaymentMethodToLoyaltyPaymentMethods } from '@fhs-legacy/frontend/src/utils/payment';

import { toLoyaltyPromotionInput } from '../utils/helpers';

import { useLoyaltyLegacyStates } from './use-loyalty-legacy-states';

/**
 * Cart context sent to the loyalty engine to evaluate the cart, shared by the estimated points and the challenges
 * evaluation: the cart entries, the subtotal after discounts, the applied offers, payment method, service mode and
 * store.
 */
export const useLoyaltyCartInput = (): ILoyaltyEstimatePointsInput => {
  const { store, serviceMode } = useLoyaltyLegacyStates();
  const { cartEntries } = useCartValues();
  const subtotalAmount = useCartTotal();
  const { checkoutPaymentMethod } = usePaymentContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const storeId = store?.number;
  const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);
  const loyaltyServiceMode = LoyaltyServiceMode[serviceMode || ''] || undefined;

  return useMemo(() => {
    const appliedIncentives = parseOffers(appliedOffers).map(toLoyaltyPromotionInput);

    return {
      cartEntries: cartEntries.reduce(parseEntry(), []),
      subtotalAmount,
      ...(appliedIncentives.length > 0 && { appliedIncentives }),
      ...(paymentMethod && { paymentMethod }),
      ...(loyaltyServiceMode && { serviceMode: loyaltyServiceMode }),
      ...(storeId && { storeId }),
    };
  }, [appliedOffers, cartEntries, loyaltyServiceMode, paymentMethod, storeId, subtotalAmount]);
};
